"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InfiniteList = void 0;
/* eslint-disable react/prop-types */
const react_1 = __importStar(require("react"));
const clsx_1 = __importDefault(require("clsx"));
const react_dom_1 = require("react-dom");
const lodash_1 = require("lodash");
const react_window_1 = require("react-window");
const react_window_infinite_loader_1 = __importDefault(require("react-window-infinite-loader"));
const ListItem_1 = __importDefault(require("@material-ui/core/ListItem"));
const ListItemText_1 = __importDefault(require("@material-ui/core/ListItemText"));
const tooltip_1 = require("../tooltip/tooltip");
const infinite_list_styles_1 = require("./infinite-list-styles");
const event_callback_hook_1 = require("../../utils/event-callback-hook");
const compose_refs_1 = require("../../utils/compose-refs");
// eslint-disable-next-line react/display-name
exports.InfiniteList = react_1.memo(react_1.forwardRef(function InfiniteList({ source, onItemClick = lodash_1.noop, labelMapper = (item) => item.name, tooltipMapper = (option) => option.tooltip, showTooltip = false, selectedIds = [], maxListHeight = 300, defaultRowHeight = 26, }, ref) {
    const classes = infinite_list_styles_1.useStyles();
    /**
     * Reference to InfiniteLoader
     */
    const loader = react_1.useRef(null);
    /**
     * Reference to VariableSizeList
     */
    const listRef = react_1.useRef(null);
    /**
     * Map of each mounted item height.
     */
    const itemHeightMap = react_1.useRef({});
    const setItemHeight = event_callback_hook_1.useEventCallback((index, size) => {
        itemHeightMap.current = { ...itemHeightMap.current, [index]: size };
    });
    const getItemHeight = event_callback_hook_1.useEventCallback((index) => itemHeightMap.current[index] || defaultRowHeight);
    /**
     * Currently focused item index. Defaults to none.
     */
    const [focusedIndex, setFocusedIndex] = react_1.useState(-1);
    /**
     * Height of VariableSizeList, dynamically changed between `defaultRowHeight` and `maxListHeight`.
     */
    const [listHeight, setListHeight] = react_1.useState(defaultRowHeight);
    const computeListHeight = event_callback_hook_1.useEventCallback((itemIndex, itemHeight) => {
        if (itemIndex !== undefined &&
            itemHeight !== undefined &&
            listRef.current) {
            setItemHeight(itemIndex, itemHeight);
            listRef.current.resetAfterIndex(itemIndex);
        }
        else {
            itemHeightMap.current = {};
        }
        let newListHeight = 0;
        for (const key in itemHeightMap.current) {
            newListHeight += itemHeightMap.current[key];
        }
        if (newListHeight > maxListHeight) {
            newListHeight = maxListHeight;
        }
        setListHeight(newListHeight);
    });
    react_1.useEffect(() => {
        computeListHeight();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source.count]);
    /**
     * Infinite list interface.
     */
    react_1.useImperativeHandle(ref, () => ({
        reset: () => {
            react_dom_1.unstable_batchedUpdates(() => {
                var _a;
                setFocusedIndex(-1);
                (_a = loader.current) === null || _a === void 0 ? void 0 : _a.resetloadMoreItemsCache(false);
                source.loadMore();
            });
        },
        focusPrevious: () => {
            var _a;
            const newIndex = Math.min(Math.max(focusedIndex - 1, 0), source.items.length - 1);
            setFocusedIndex(newIndex);
            (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollToItem(newIndex);
        },
        focusNext: () => {
            var _a;
            const newIndex = Math.min(Math.max(focusedIndex + 1, 0), source.items.length - 1);
            setFocusedIndex(newIndex);
            (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.scrollToItem(newIndex);
        },
        getFocusedItem: () => {
            return focusedIndex !== -1 ? source.items[focusedIndex] : undefined;
        },
    }), [focusedIndex, source]);
    const renderItem = react_1.useCallback((props) => {
        const { index, style } = props;
        const id = index < source.items.length ? source.items[index].id : undefined;
        const value = index < source.items.length
            ? labelMapper(source.items[index], index)
            : '';
        const tooltip = index < source.items.length ? tooltipMapper(source.items[index]) : '';
        const renderedItem = (react_1.default.createElement(ListItem_1.default, { onClick: () => index < source.items.length &&
                onItemClick(source.items[index], index), button: true, style: style, key: index, classes: {
                root: clsx_1.default(classes.item, {
                    [classes.itemSelected]: id !== undefined && selectedIds.includes(id),
                    [classes.itemFocused]: index === focusedIndex,
                }),
            } },
            react_1.default.createElement(ListItemText_1.default, { disableTypography: true, primary: value, ref: (item) => {
                    if (item &&
                        item.clientHeight !== itemHeightMap.current[index]) {
                        computeListHeight(index, item.clientHeight);
                    }
                } })));
        if (showTooltip) {
            return react_1.default.createElement(tooltip_1.Tooltip, { title: tooltip }, renderedItem);
        }
        else {
            return renderedItem;
        }
    }, [
        source.items,
        labelMapper,
        tooltipMapper,
        classes.item,
        classes.itemSelected,
        classes.itemFocused,
        selectedIds,
        focusedIndex,
        showTooltip,
        onItemClick,
        computeListHeight,
    ]);
    const totalCount = source.hasNextPage()
        ? source.items.length + 1
        : source.items.length;
    const isItemLoaded = react_1.useCallback((index) => {
        return source.isDataValid() && index < source.items.length;
    }, [source]);
    const loadMoreItems = source.loading ? async () => lodash_1.noop() : source.loadMore;
    return (react_1.default.createElement(react_window_infinite_loader_1.default, { ref: loader, isItemLoaded: isItemLoaded, itemCount: totalCount, loadMoreItems: loadMoreItems, threshold: 1 }, ({ onItemsRendered, ref }) => {
        const composedRef = compose_refs_1.composeRefs(ref, listRef);
        return (react_1.default.createElement(react_window_1.VariableSizeList, { height: listHeight, width: "100%", itemSize: getItemHeight, itemCount: totalCount, estimatedItemSize: defaultRowHeight, ref: composedRef, onItemsRendered: onItemsRendered }, renderItem));
    }));
}));
//# sourceMappingURL=infinite-list.js.map